import { baseApi } from '@shared/api/baseApi'
import { getActiveApiBaseUrl } from '@shared/api/apiHost'
import { ENV } from '@shared/config/env'
import { getPlatform } from '@shared/platform'
import type { AnalyticsEvent, TrackEventsResponse } from '../types'

/**
 * Больше 50 событий в запросе — `422` на весь батч, а не «примем 50».
 * Поэтому режем сами.
 */
export const MAX_EVENTS_PER_BATCH = 50

/** Отправить один батч событий (не пустой, ≤ 50). */
export const trackEvents = async (
  events: AnalyticsEvent[]
): Promise<TrackEventsResponse | null> => {
  if (events.length === 0) return null
  const { data } = await baseApi.post<TrackEventsResponse>(
    '/api/track/events',
    { events }
  )
  return data
}

/**
 * Отправить события пачками по ≤ 50, не дожидаясь результата.
 * Ошибки не всплывают: аналитика не должна ломать работу приложения.
 */
export const sendEventsBatched = (events: AnalyticsEvent[]): void => {
  for (let i = 0; i < events.length; i += MAX_EVENTS_PER_BATCH) {
    void trackEvents(events.slice(i, i + MAX_EVENTS_PER_BATCH)).catch(() => {})
  }
}

/**
 * Отправка, переживающая уход со страницы.
 *
 * Обычный запрос браузер обрывает вместе со страницей — а именно в этот момент
 * уходит самое ценное: остаток времени на странице и хвост действий. Флаг
 * `keepalive` разрешает запросу дожить до конца уже без страницы.
 *
 * Через axios так нельзя, поэтому здесь голый `fetch`. Заголовок с данными
 * Telegram подставляем руками — перехватчика у нас тут нет.
 */
export const sendEventsKeepalive = (events: AnalyticsEvent[]): void => {
  if (events.length === 0) return

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (ENV.IS_TG) {
    const initData = getPlatform().getInitData()
    if (initData) headers['Init-Data'] = initData
  }

  for (let i = 0; i < events.length; i += MAX_EVENTS_PER_BATCH) {
    const chunk = events.slice(i, i + MAX_EVENTS_PER_BATCH)
    try {
      void fetch(`${getActiveApiBaseUrl()}/api/track/events`, {
        method: 'POST',
        headers,
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({ events: chunk }),
      }).catch(() => {})
    } catch {
      // Аналитика не должна мешать уходу со страницы.
    }
  }
}
